import z from "zod";

import { AuditTrailEntryKind } from "./AuditTrailEntryKind.js";

/**
 * The payload each kind of entry carries, keyed by entry kind.
 *
 * Two properties of these schemas are load-bearing, and they pull in opposite
 * directions:
 *
 * - **A write is refused unless its payload matches.** Haven's trail has no
 *   inherited rows, so a schema can be the gate rather than a description of
 *   whatever got written — which is the only reason a read is allowed to be as
 *   forgiving as the next point makes it.
 * - **A read never tightens.** An entry is a historical snapshot: it records what
 *   was written when the action happened, which may predate the current types. So
 *   a field is made optional or removed, never re-required or re-typed, and a
 *   field that stops being written becomes `.nullish()`: a writer passing a value
 *   it read from a nullable column stores an explicit `null`, which reads back as
 *   one, and bare `.optional()` rejects it.
 *
 * A value from a closed set is typed as `z.string()` rather than an enum, so
 * renaming one in the application cannot make its stored rows unreadable. Nothing
 * narrows it on the way in either — a payload field's own documentation is the
 * whole statement of what a writer should put there.
 *
 * The registry is **total**, not partial: every entry kind has a schema, checked
 * by `satisfies`. That is what makes `AuditDataFor` always precise, so nothing
 * needs a loose fallback and there is no "not modelled yet" state for a reader
 * to handle.
 */

/** For an action whose only content is that it happened. */
const noPayload = z.object({});

/**
 * How an account an administrator created will sign in, `sso` or `password`,
 * as decided when it was created. A domain can move between the two later, so
 * the account cannot be relied on to say this afterwards. Absent where the
 * account was created by its owner signing in.
 */
export const userCreatedDataSchema = z.object({
  route: z.string().optional(),
});

export const userPasswordResetSentDataSchema = noPayload;

export const userLinkedToIdentityProviderDataSchema = noPayload;

export const sessionSignInSuccessDataSchema = z.object({
  /**
   * Whether the session was established as a Thalamos administrator session.
   * Recorded because it decides what the session may reach, and is derived from
   * the address the provider vouched for rather than from anything the user
   * chose.
   */
  isAdminSession: z.boolean(),
});

export const sessionSignInFailureDataSchema = z.object({
  /**
   * Why the sign-in was refused, as the server decided it — not as it was shown
   * to the browser, which is told nothing so that sign-in cannot be used to
   * discover who is in the directory group.
   */
  reason: z.string(),
});

export const sessionSignedOutDataSchema = noPayload;

/**
 * How the context came to be chosen — `picker` when the user selected it, `link`
 * when a link issued for that context named it.
 */
const chosenVia = z.string();

/**
 * One shape per era, each requiring its own field. `role` is retired but stays
 * required in its era's shape rather than going `.nullish()`, so an entry must
 * still say which context was chosen.
 */
export const sessionContextChosenDataSchema = z.union([
  z.object({
    /** The `SessionContextKind` chosen; the roles it combined are on the entry's own `roles`. */
    kind: z.string(),
    via: chosenVia,
  }),
  z.object({
    /** The single role chosen, in entries from before a context was a kind of work. */
    role: z.string(),
    via: chosenVia,
  }),
]);

export const userProfileUpdatedDataSchema = z.object({
  /** The name the account was set to. */
  name: z.string(),
});

/**
 * Both membership entries carry the role, so a trail read years later says what
 * was granted without depending on the role set still containing it.
 */
const administrativeMembershipDataSchema = z.object({
  role: z.string(),
});

export const administrativeMembershipGrantedDataSchema =
  administrativeMembershipDataSchema;

export const administrativeMembershipRevokedDataSchema =
  administrativeMembershipDataSchema;

/**
 * A membership granted or revoked at a customer organisation.
 *
 * The role only. Who acted, in what session context, and when are the
 * envelope's; the person it was granted to and the organisation it is held at
 * are named resources on the entry, which is how the trail is searched by
 * either. Copying them here would say the same thing twice and let the two
 * disagree.
 */
const customerMembershipDataSchema = z.object({
  role: z.string(),
});

export const membershipGrantedDataSchema = customerMembershipDataSchema;

export const membershipRevokedDataSchema = customerMembershipDataSchema;
export const personCreatedDataSchema = noPayload;

export const personUpdatedDataSchema = z.object({
  /**
   * Which parts of the record changed — `names`, `dateOfBirth`. An identifier
   * is never among them: one is stated when a record is registered and a
   * correction cannot change it.
   *
   * The values themselves are not carried: the record's history tables hold
   * every version, and a child's names and identifiers do not belong in a trail
   * that is exported and read more widely than the record is.
   */
  fields: z.array(z.string()),

  /**
   * The version the change left the record at. It names the version of the
   * record and of its names together, so recovering what the record then said
   * is a lookup on the number rather than a correlation of instants across the
   * record's history tables.
   */
  version: z.number(),
});

/**
 * Both merge entries carry both ids, though both records are also named as
 * resources: the resources say which records were touched, and only the payload
 * says which one survived.
 *
 * There is deliberately no reason here. Why two children were declared to be
 * one is prose somebody typed about them, and it lives on the merge row where
 * reach governs who reads it; the entry names that row as a resource instead.
 * A reason recorded here before that was decided reads back as `undefined`,
 * which is why nothing requires one.
 */
const personMergeDataSchema = z.object({
  sourcePersonId: z.string(),
  targetPersonId: z.string(),
});

export const personMergedDataSchema = personMergeDataSchema;

export const personUnmergedDataSchema = personMergeDataSchema;

/**
 * Nothing but the act: who was named is said by the involvement entries the
 * same act writes, each naming its own row.
 */
export const personEnrolledDataSchema = noPayload;

export const personArchivedDataSchema = noPayload;

export const personUnarchivedDataSchema = noPayload;

/** Nothing but the act: the record is the entry's resource. */
export const adminPersonReadDataSchema = noPayload;

/**
 * A scope as it was left. `parentScopeId` is nullish rather than nullable: a
 * scope at the top of a chain has none, and that arrives from a nullable column
 * as an explicit null.
 */
const configurationScopeDataSchema = z.object({
  name: z.string(),
  parentScopeId: z.string().nullish(),
});

export const configurationScopeCreatedDataSchema = configurationScopeDataSchema;

export const configurationScopeUpdatedDataSchema = configurationScopeDataSchema;

export const configurationOrganisationScopeChangedDataSchema = z.object({
  /** The scope the organisation was attached to, or null where it was detached. */
  configurationScopeId: z.string().nullish(),
});

/**
 * The value is carried as JSON text rather than as itself.
 *
 * A setting's value is a capability, a choice, or a set of them, and what shapes
 * exist changes as settings are added — but an entry is a historical snapshot,
 * and a schema that admitted only today's shapes would stop old rows parsing the
 * first time a new one appeared. Text is the shape that never has to change, and
 * it reads perfectly well: `["en","cy"]` says what happened.
 */
export const configurationSettingSetDataSchema = z.object({
  key: z.string(),
  value: z.string(),
});

export const configurationSettingClearedDataSchema = z.object({
  key: z.string(),
});

/**
 * The kind of involvement, and nothing about who held it: the party is named as
 * a resource, and naming it here too would say the same thing twice and let the
 * two disagree.
 */
export const involvementStartedDataSchema = z.object({
  kind: z.string(),
});

/**
 * The kind, why it ended, as `InvolvementEndedCauseSchema` spells it, and the
 * instant it stops conveying anything, which may be later than the entry. Who
 * ended it is the entry's author, so it is not repeated here.
 */
export const involvementEndedDataSchema = z.object({
  kind: z.string(),
  cause: z.string(),
  endedAt: z.iso.datetime({ offset: true }).optional(),
});

/**
 * What an assessment was started against, and whether it is the definitive one.
 *
 * No name, address or answer: the entry names the record by id through its
 * resources. The digest is here because the name alone does not say which bytes
 * were pinned, and that is the question a reader of an old assessment has.
 */
export const assessmentStartedDataSchema = z.object({
  instrument: z.string(),
  instrumentDigest: z.string(),
  /** The version the young person's record was at when this was started. */
  personVersion: z.number().int(),
  /**
   * Whether this is the definitive assessment rather than a contribution.
   * Optional because entries recorded before it was stated still have to read.
   */
  definitive: z.boolean().optional(),
});

/**
 * The instrument version an act on an assessment was taken against, so each
 * entry says it without a reader tracing it back to the start.
 *
 * Optional because entries recorded before it was stated still have to read.
 * The version is pinned for the assessment's life, so an entry that lacks it
 * means the one its `assessment_started` entry names.
 */
const pinnedInstrumentFields = {
  instrument: z.string().optional(),
  instrumentDigest: z.string().optional(),
};

/**
 * Which attempt this was, against which version, and whether it waits for an
 * approver.
 *
 * No part of the document, and not the sender's own reference either. The trail
 * is read by people who never learn who the child is, and a reference
 * is free text somebody else chose — a case number to them, a child's name if
 * they put one there. Whether it holds anything about her is not knowable here,
 * so it stays on the submission, under reach, and the trail says only that an
 * attempt was made, which one it was, against which version, and whether it
 * completed the assessment.
 */
export const assessmentSubmittedDataSchema = z.object({
  submissionNumber: z.number().int(),
  /**
   * Whether the attempt waits for an approver, or completed the assessment.
   * Optional because entries recorded before it was stated still have to read.
   */
  definitive: z.boolean().optional(),
  ...pinnedInstrumentFields,
});

/**
 * Which submission a draft was started from, or which assessment it carried
 * ratings over from, where it was not started empty, and nothing of what it
 * holds: a draft holds answers about a child, and the trail is read by people
 * who reach no record at all.
 */
export const assessmentDraftStartedDataSchema = z.object({
  fromSubmissionNumber: z.number().int().optional(),
  carriedOverFromAssessmentId: z.string().optional(),
});

/**
 * Deleting an assessment, or undeleting it.
 *
 * Nothing but the act. Why it was deleted is prose about an assessment and so
 * about a child: it stays on the record, where reach governs who reads it, and
 * the trail is read by people who reach no record at all.
 */
export const assessmentDeletionDataSchema = z.object({});

/** Which version the downloaded assessment was answered against. */
export const assessmentPdfDownloadedDataSchema = z.object({
  instrument: z.string(),
  instrumentDigest: z.string(),
});

/** Which version the assessment a guest opened was answered against. */
export const sharedAssessmentReadDataSchema = assessmentPdfDownloadedDataSchema;

/** Which assessments were shared is the entry's resources. */
export const guestAssessmentsSharedDataSchema = z.object({
  assessmentCount: z.number().int(),
});

/**
 * Which submission an approver decided, against which version, and nothing
 * else.
 *
 * The outcome is the entry's kind. A return's reason is prose about an
 * assessment and so about a child: it stays on the decision, where reach
 * governs who reads it, and the trail is read by people who reach no record at
 * all.
 */
export const assessmentDecidedDataSchema = z.object({
  submissionNumber: z.number().int(),
  ...pinnedInstrumentFields,
});

/** Which returned submission an assessment was reopened from, against which version. */
export const assessmentReopenedDataSchema = z.object({
  submissionNumber: z.number().int(),
  ...pinnedInstrumentFields,
});

/**
 * The kind an organisation was given, and the kind it had before, as
 * `OrganisationType` spells them. The organisation is the entry's resource.
 */
export const organisationKindSetDataSchema = z.object({
  kind: z.string(),
  previousKind: z.string(),
});

/**
 * The kind a new organisation was given, as `OrganisationType` spells it. Its
 * name travels as the resource's name rather than in the payload, so the trail
 * states it once.
 */
export const organisationCreatedDataSchema = z.object({
  kind: z.string(),
});

/**
 * Whether the organisation now reads what its members' finished assessments
 * say, and whether it did before. The organisation is the entry's resource.
 */
export const organisationReadsApprovedAssessmentsThroughMembersSetDataSchema =
  z.object({
    value: z.boolean(),
    previousValue: z.boolean(),
  });

/**
 * What the organisation was called before. Its new name travels as the
 * resource's name, so the trail states it once.
 */
export const organisationRenamedDataSchema = z.object({
  previousName: z.string(),
});

/**
 * A relationship between two organisations as it is stored: `kind` as
 * `OrganisationRelationshipKind` spells it, read from `organisationId`'s side,
 * so "A `member_of` B" says which of the two is the member. Both organisations
 * are also the entry's resources, named as they were called at the time.
 */
const organisationRelationshipDataSchema = z.object({
  kind: z.string(),
  organisationId: z.string(),
  relatedOrganisationId: z.string(),
});

export const organisationRelationshipAddedDataSchema =
  organisationRelationshipDataSchema;

export const organisationRelationshipRemovedDataSchema =
  organisationRelationshipDataSchema;

/**
 * What a guest was given access for, such as `complete_assessment`, as
 * `GuestAccessPurposeSchema` spells it.
 */
export const guestAccessLinkCreatedDataSchema = z.object({
  purpose: z.string(),
});

export const guestSessionCodeSentDataSchema = noPayload;

/**
 * The device and browser a guest signed in from, as read from the request's
 * user agent: what the guest's sharer is owed sight of, without the agent
 * string itself.
 */
const guestDeviceFields = {
  device: z.string(),
  browser: z.string(),
};

/**
 * Which step was wrong, `code` or `date_of_birth`, and how many wrong tries
 * the link had then taken, counting this one.
 */
export const guestSessionSignInFailureDataSchema = z.object({
  step: z.string(),
  wrongTries: z.number(),
  ...guestDeviceFields,
});

export const guestSessionSignInSuccessDataSchema = z.object(guestDeviceFields);

export const guestAccessLinkLockedDataSchema = noPayload;

/**
 * Which wording was accepted, by purpose and by the digest its words are kept
 * under in `agreement_wordings`.
 */
export const guestSessionAgreementAcceptedDataSchema = z.object({
  purpose: z.string(),
  wordingDigest: z.string(),
});

export const guestAccessLinkDeclinedDataSchema = noPayload;

export const auditDataSchemaRegistry = {
  [AuditTrailEntryKind.user_created]: userCreatedDataSchema,
  [AuditTrailEntryKind.user_password_reset_sent]:
    userPasswordResetSentDataSchema,
  [AuditTrailEntryKind.user_linked_to_identity_provider]:
    userLinkedToIdentityProviderDataSchema,
  [AuditTrailEntryKind.session_sign_in_success]: sessionSignInSuccessDataSchema,
  [AuditTrailEntryKind.session_sign_in_failure]: sessionSignInFailureDataSchema,
  [AuditTrailEntryKind.session_signed_out]: sessionSignedOutDataSchema,
  [AuditTrailEntryKind.session_context_chosen]: sessionContextChosenDataSchema,
  [AuditTrailEntryKind.user_profile_updated]: userProfileUpdatedDataSchema,
  [AuditTrailEntryKind.administrative_membership_granted]:
    administrativeMembershipGrantedDataSchema,
  [AuditTrailEntryKind.administrative_membership_revoked]:
    administrativeMembershipRevokedDataSchema,
  [AuditTrailEntryKind.membership_granted]: membershipGrantedDataSchema,
  [AuditTrailEntryKind.membership_revoked]: membershipRevokedDataSchema,
  [AuditTrailEntryKind.person_created]: personCreatedDataSchema,
  [AuditTrailEntryKind.person_enrolled]: personEnrolledDataSchema,
  [AuditTrailEntryKind.person_updated]: personUpdatedDataSchema,
  [AuditTrailEntryKind.person_merged]: personMergedDataSchema,
  [AuditTrailEntryKind.person_unmerged]: personUnmergedDataSchema,
  [AuditTrailEntryKind.person_archived]: personArchivedDataSchema,
  [AuditTrailEntryKind.person_unarchived]: personUnarchivedDataSchema,
  [AuditTrailEntryKind.admin_person_read]: adminPersonReadDataSchema,
  [AuditTrailEntryKind.assessment_started]: assessmentStartedDataSchema,
  [AuditTrailEntryKind.assessment_draft_started]:
    assessmentDraftStartedDataSchema,
  [AuditTrailEntryKind.assessment_submitted]: assessmentSubmittedDataSchema,
  [AuditTrailEntryKind.assessment_deleted]: assessmentDeletionDataSchema,
  [AuditTrailEntryKind.assessment_undeleted]: assessmentDeletionDataSchema,
  [AuditTrailEntryKind.assessment_pdf_downloaded]:
    assessmentPdfDownloadedDataSchema,
  [AuditTrailEntryKind.shared_assessment_read]: sharedAssessmentReadDataSchema,
  [AuditTrailEntryKind.assessment_approved]: assessmentDecidedDataSchema,
  [AuditTrailEntryKind.assessment_returned]: assessmentDecidedDataSchema,
  [AuditTrailEntryKind.assessment_reopened]: assessmentReopenedDataSchema,
  [AuditTrailEntryKind.configuration_scope_created]:
    configurationScopeCreatedDataSchema,
  [AuditTrailEntryKind.configuration_scope_updated]:
    configurationScopeUpdatedDataSchema,
  [AuditTrailEntryKind.configuration_organisation_scope_changed]:
    configurationOrganisationScopeChangedDataSchema,
  [AuditTrailEntryKind.configuration_setting_set]:
    configurationSettingSetDataSchema,
  [AuditTrailEntryKind.configuration_setting_cleared]:
    configurationSettingClearedDataSchema,
  [AuditTrailEntryKind.involvement_started]: involvementStartedDataSchema,
  [AuditTrailEntryKind.involvement_ended]: involvementEndedDataSchema,
  [AuditTrailEntryKind.organisation_kind_set]: organisationKindSetDataSchema,
  [AuditTrailEntryKind.organisation_created]: organisationCreatedDataSchema,
  [AuditTrailEntryKind.organisation_reads_approved_assessments_through_members_set]:
    organisationReadsApprovedAssessmentsThroughMembersSetDataSchema,
  [AuditTrailEntryKind.organisation_renamed]: organisationRenamedDataSchema,
  [AuditTrailEntryKind.organisation_relationship_added]:
    organisationRelationshipAddedDataSchema,
  [AuditTrailEntryKind.organisation_relationship_removed]:
    organisationRelationshipRemovedDataSchema,
  [AuditTrailEntryKind.guest_link_created]: guestAccessLinkCreatedDataSchema,
  [AuditTrailEntryKind.guest_assessments_shared]:
    guestAssessmentsSharedDataSchema,
  [AuditTrailEntryKind.guest_code_sent]: guestSessionCodeSentDataSchema,
  [AuditTrailEntryKind.guest_session_sign_in_failure]:
    guestSessionSignInFailureDataSchema,
  [AuditTrailEntryKind.guest_session_sign_in_success]:
    guestSessionSignInSuccessDataSchema,
  [AuditTrailEntryKind.guest_link_locked]: guestAccessLinkLockedDataSchema,
  [AuditTrailEntryKind.guest_agreement_accepted]:
    guestSessionAgreementAcceptedDataSchema,
  [AuditTrailEntryKind.guest_link_declined]: guestAccessLinkDeclinedDataSchema,
  // `satisfies` rather than an annotation: the check closes the set without
  // widening the value types, so each key stays bound to its own schema and
  // `AuditDataFor` can infer the payload rather than collapsing to `unknown`.
} satisfies Record<AuditTrailEntryKind, z.ZodType>;

export type AuditDataRegistry = typeof auditDataSchemaRegistry;

/** The payload type for an entry kind. */
export type AuditDataFor<T extends AuditTrailEntryKind> = z.infer<
  AuditDataRegistry[T]
>;

/**
 * Makes the payload argument optional exactly when `{}` satisfies it, so an
 * action with nothing to record is written without a trailing `{}` while one
 * that carries a required field cannot be written without it.
 */
export type AuditDataArg<T extends AuditTrailEntryKind> =
  Record<string, never> extends AuditDataFor<T>
    ? [data?: AuditDataFor<T>]
    : [data: AuditDataFor<T>];
